import { useState, useEffect, useLayoutEffect, useRef, ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import React from "react";
import { useSearch, useNavigate } from "@tanstack/react-router";
import {
  RefreshCcwIcon,
  Search,
  Plus,
  Upload,
  Loader2,
  Filter,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { modulesTypes } from "@/convex/system/permissions";
import { usePermissions } from "@/hooks/usePermissions";
import { Skeleton } from "@/components/ui/skeleton";
import { DateRangePicker } from "../ui/date-range-picker";
import { NumericRangePicker } from "../ui/numeric-range-picker";
import { cn, getNumericRange, updateNumericRange } from "@/lib/utils";
import DynamicIcon from "../utils/lucide-icon";
import { useSearchLoading } from "@/hooks/useSearchLoading";
import { Badge } from "../ui/badge";
import * as Icons from "lucide-react";
import { useDebounce } from "@/hooks/utils/useDebounce";
import { useCommonI18n } from "@/hooks/useModuleI18n";

type IconName = keyof typeof Icons;

export interface Filter {
  id: string;
  name: string;
  options?: Record<string, any>[];
  defaultValue?: string;
  type?: "date" | "date_range" | "numeric_range" | "number" | "string";
  icon?: IconName;
  min?: number;
  max?: number;
  // Props adicionales para numeric_range
  prefix?: string;
  suffix?: string;
  decimalPlaces?: number;
  allowDecimals?: boolean;
  // Custom component replacement for select filters
  customComponent?: React.FC<{
    value: string;
    onChange: (value: string) => void;
    options: Record<string, any>[];
    filterName: string;
  }>;
}

export interface HeaderAction {
  label: string;
  icon?: LucideIcon;
  onClick: () => void;
  variant?: "default" | "secondary" | "outline" | "ghost" | "destructive";
}

export const TableFilters = ({
  setIsModalOpen,
  updateFilters,
  filters,
  table,
  tableName,
  showImportButton = false,
  onImportClick,
  rootPath,
  createRoute,
  defaultPageSize = 5,
  headerActions = [],
}: {
  setIsModalOpen?: (open: boolean) => void;
  updateFilters: (filters: Record<string, any>) => void;
  filters: Filter[];
  table: typeof modulesTypes.type;
  tableName: string;
  showImportButton?: boolean;
  onImportClick?: () => void;
  rootPath?: string;
  createRoute?: string;
  defaultPageSize?: number;
  headerActions?: HeaderAction[];
}) => {
  const search = useSearch({ strict: false }) as Record<string, any>;
  const navigate = useNavigate();
  const { can, isLoading: permissionsLoading } = usePermissions(table);
  const { isSearchLoading } = useSearchLoading();
  const { t } = useCommonI18n();

  const [searchTerm, setSearchTerm] = useState(search.search || "");
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // Leer valores de fecha directamente de search
  const targetDateFrom = search.targetDateFrom || "";
  const targetDateTo = search.targetDateTo || "";
  const numericRangesParam = search.numericRanges || "";

  const filterActionRef = useRef(false);

  const filterStorageKey = `table-filters-${table}`;
  const pageSizeStorageKey = `table-page-size-${table}`;

  useEffect(() => {
    const filtersToSave: Record<string, any> = {};
    for (const filter of filters) {
      const value = search[filter.id];
      if (value && value !== "all") {
        filtersToSave[filter.id] = value;
      }
    }
    if (Object.keys(filtersToSave).length > 0) {
      localStorage.setItem(filterStorageKey, JSON.stringify(filtersToSave));
    } else {
      localStorage.removeItem(filterStorageKey);
    }
  }, [search, filters, filterStorageKey]);

  const getStoredPageSize = (): number | null => {
    const stored = localStorage.getItem(pageSizeStorageKey);
    if (!stored) return null;
    const parsed = Number(stored);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  };

  const persistPageSize = (value: number) => {
    localStorage.setItem(pageSizeStorageKey, String(value));
  };

  const resolvedPageSize =
    search.targetMaxRows !== undefined
      ? Number(search.targetMaxRows) || defaultPageSize
      : (getStoredPageSize() ?? defaultPageSize);

  useLayoutEffect(() => {
    if (filterActionRef.current) {
      filterActionRef.current = false;
      return;
    }

    if (search.targetMaxRows === undefined) {
      const stored = getStoredPageSize();
      if (stored && stored !== defaultPageSize) {
        filterActionRef.current = true;
        navigate({
          to: rootPath || `/${table}`,
          search: (prev: any) => ({ ...prev, targetMaxRows: stored }),
          replace: true,
        });
      }
    }
  }, [search, pageSizeStorageKey, rootPath, table, navigate, defaultPageSize]);

  useLayoutEffect(() => {
    if (filterActionRef.current) {
      filterActionRef.current = false;
      return;
    }

    const hasFilterParams = Object.keys(search).some(
      (k) =>
        k !== "page" &&
        k !== "targetMaxRows" &&
        k !== "numericRanges" &&
        search[k],
    );

    if (!hasFilterParams) {
      const saved = localStorage.getItem(filterStorageKey);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          const activeFilters: Record<string, any> = {};
          for (const [key, value] of Object.entries(parsed)) {
            if (value && value !== "all") {
              activeFilters[key] = value;
            }
          }
          if (Object.keys(activeFilters).length > 0) {
            navigate({
              to: rootPath || `/${table}`,
              search: (prev: any) => ({ ...prev, ...activeFilters }),
              replace: true,
            });
          }
        } catch {
          localStorage.removeItem(filterStorageKey);
        }
      }
    }
  }, [search, filterStorageKey, rootPath, table, navigate]);

  const handleSearchTermChange = (value: string) => {
    filterActionRef.current = true;
    navigate({
      to: rootPath || `/${table}`,
      search: (prev: any) => ({
        ...prev,
        search: value || undefined,
        page: 1, // Resetear a página 1 cuando se realiza una búsqueda
      }),
    });
  };

  const debouncedSearch = useDebounce((value: string) => {
    handleSearchTermChange(value);
  }, 500);

  const handleSearchInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setSearchTerm(event.target.value);
    debouncedSearch(event.target.value);
  };

  const handleNewItem = () => {
    if (!can("create")) return;

    // Usar createRoute si está definida (para creación por ruta)
    if (createRoute) {
      navigate({ to: createRoute as any });
    }
    // Usar modal si setIsModalOpen está definido (para creación por modal)
    else if (setIsModalOpen) {
      setIsModalOpen(true);
    }
  };

  const handleNumericRangeUpdate = (
    filterId: string,
    min: number | undefined,
    max: number | undefined,
  ) => {
    filterActionRef.current = true;
    const newNumericRanges = updateNumericRange(
      numericRangesParam,
      filterId,
      min,
      max,
    );

    navigate({
      to: rootPath || `/${table}`,
      search: (prev: any) => ({
        ...prev,
        numericRanges: newNumericRanges || undefined,
        page: 1, // Resetear a página 1 cuando se actualizan filtros numéricos
      }),
    });
  };

  const handleResetFilters = () => {
    filterActionRef.current = true;
    localStorage.removeItem(filterStorageKey);
    localStorage.removeItem(pageSizeStorageKey);
    navigate({
      to: rootPath || `/${table}`,
      search: {},
    });

    // Resetear el estado local del searchTerm
    setSearchTerm("");
  };

  return (
    <TooltipProvider>
      <div className="flex flex-col gap-3">
        {/* Fila 1: Búsqueda y Filtros */}
        <div className="flex flex-col md:flex-row gap-2 md:items-center md:justify-between">
          {/* Container izquierdo: Búsqueda, Filtros y Acciones móviles */}
          <div className="flex flex-col md:flex-row gap-2 md:items-center md:flex-1 lg:min-w-0">
            {/* Barra de búsqueda con acciones móviles */}
            <div className="flex items-center gap-2 w-full">
              <div className="flex-1">
                <InputGroup className="w-full md:min-w-[180px] md:max-w-[250px]">
                  <InputGroupInput
                    placeholder={t("table.filters.searchPlaceholder")}
                    onChange={handleSearchInputChange}
                    value={searchTerm}
                  />
                  <InputGroupAddon>
                    <Search className="h-4 w-4" />
                  </InputGroupAddon>
                </InputGroup>
              </div>

              {/* Botón de menú filtros móvil */}
              <div className="flex lg:hidden items-center gap-1">
                <Popover
                  open={mobileFiltersOpen}
                  onOpenChange={setMobileFiltersOpen}
                >
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-9 w-9"
                        >
                          <Filter className="h-4 w-4" />
                        </Button>
                      </PopoverTrigger>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{t("table.filters.title")}</p>
                    </TooltipContent>
                  </Tooltip>
                  <PopoverContent
                    className="w-80 max-h-[70vh] overflow-y-auto"
                    align="end"
                  >
                    <div className="space-y-4">
                      <div className="font-medium text-sm">
                        {t("table.filters.title")}
                      </div>

                      {/* Filtros regulares */}
                      {filters
                        .filter((f) => f.type !== "numeric_range")
                        .map((filter) => (
                          <div key={filter.id} className="space-y-2">
                            <label className="text-xs font-medium text-muted-foreground">
                              {filter.name}
                            </label>
                            {filter.customComponent ? (
                              React.createElement(filter.customComponent, {
                                value:
                                  search[filter.id] ||
                                  filter.defaultValue ||
                                  "all",
                                onChange: (value: string) => {
                                  filterActionRef.current = true;
                                  if (value === "all") {
                                    updateFilters({ [filter.id]: undefined });
                                  } else {
                                    updateFilters({ [filter.id]: value });
                                  }
                                  setMobileFiltersOpen(false);
                                },
                                options: filter.options || [],
                                filterName: filter.name,
                              })
                            ) : (
                              <Select
                                value={
                                  filter.options?.find(
                                    (option) =>
                                      option.value === search[filter.id],
                                  )?.value || filter.defaultValue
                                }
                                onValueChange={(value) => {
                                  filterActionRef.current = true;
                                  if (value === "all") {
                                    updateFilters({ [filter.id]: undefined });
                                  } else {
                                    updateFilters({ [filter.id]: value });
                                  }
                                  setMobileFiltersOpen(false);
                                }}
                              >
                                <SelectTrigger className="w-full">
                                  {filter.icon && (
                                    <DynamicIcon name={filter.icon} />
                                  )}
                                  <SelectValue placeholder={filter.name} />
                                </SelectTrigger>
                                <SelectContent>
                                  {filter.options?.map((option) => (
                                    <SelectItem
                                      key={option.value}
                                      value={option.value}
                                    >
                                      {option.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          </div>
                        ))}

                      {/* Filtros de rango numérico */}
                      {filters
                        .filter((f) => f.type === "numeric_range")
                        .map((filter) => {
                          const currentRange = getNumericRange(
                            numericRangesParam,
                            filter.id,
                          );

                          return (
                            <div key={filter.id} className="space-y-2">
                              <label className="text-xs font-medium text-muted-foreground">
                                {filter.name}
                              </label>
                              <NumericRangePicker
                                label={filter.name}
                                prefix={filter.prefix || "L. "}
                                suffix={filter.suffix || ""}
                                allowDecimals={filter.allowDecimals ?? true}
                                decimalPlaces={filter.decimalPlaces ?? 2}
                                initialMin={currentRange?.min ?? filter.min}
                                initialMax={currentRange?.max ?? filter.max}
                                onUpdate={({ range }) => {
                                  handleNumericRangeUpdate(
                                    filter.id,
                                    range.min,
                                    range.max,
                                  );
                                  setMobileFiltersOpen(false);
                                }}
                              />
                            </div>
                          );
                        })}

                      {/* Control de filas por página */}
                      <div className="space-y-2">
                        <label className="text-xs font-medium text-muted-foreground">
                          {t("table.filters.show")}
                        </label>
                        <Select
                          onValueChange={(value) => {
                            const numericValue = Number(value);
                            filterActionRef.current = true;
                            persistPageSize(numericValue);
                            updateFilters({ targetMaxRows: numericValue });
                            setMobileFiltersOpen(false);
                          }}
                          value={String(resolvedPageSize)}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue
                              placeholder={t("table.filters.show")}
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {[5, 10, 20, 50].map((rows) => (
                              <SelectItem key={rows} value={rows.toString()}>
                                {rows} {t("table.filters.rows")}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Date Range Picker */}
                      <div className="space-y-2">
                        <label className="text-xs font-medium text-muted-foreground">
                          {t("table.filters.dateRange")}
                        </label>
                        <DateRangePicker
                          key={`${targetDateFrom}-${targetDateTo}`}
                          onUpdate={(values) => {
                            filterActionRef.current = true;
                            updateFilters({
                              targetDateFrom: values.range.from.toString(),
                              targetDateTo: values.range?.to?.toString() || "",
                            });
                            setMobileFiltersOpen(false);
                          }}
                          initialDateFrom={
                            targetDateFrom
                              ? new Date(Number(targetDateFrom))
                              : undefined
                          }
                          initialDateTo={
                            targetDateTo
                              ? new Date(Number(targetDateTo))
                              : undefined
                          }
                          align="start"
                          locale="en-GB"
                          showCompare={false}
                        />
                      </div>

                      {/* Botón reset */}
                      <Button
                        variant="outline"
                        onClick={() => {
                          handleResetFilters();
                          setMobileFiltersOpen(false);
                        }}
                        className="w-full"
                      >
                        <RefreshCcwIcon className="h-4 w-4 mr-2" />
                        {t("table.filters.resetFilters")}
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>

                {headerActions.map((action, index) => {
                  const Icon = action.icon;
                  return (
                    <Tooltip key={`mobile-action-${index}`}>
                      <TooltipTrigger asChild>
                        <Button
                          variant={action.variant ?? "outline"}
                          size="icon"
                          onClick={action.onClick}
                          className="h-9 w-9"
                        >
                          {Icon && <Icon className="h-4 w-4" />}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{action.label}</p>
                      </TooltipContent>
                    </Tooltip>
                  );
                })}

                {showImportButton &&
                  onImportClick &&
                  (permissionsLoading ? (
                    <Skeleton className="h-9 w-9 rounded-md" />
                  ) : can("create") ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="secondary"
                          size="icon"
                          onClick={onImportClick}
                          className="h-9 w-9"
                        >
                          <Upload className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>
                          {t("table.filters.import")} {tableName}s
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  ) : null)}

                {(setIsModalOpen || createRoute) &&
                  (permissionsLoading ? (
                    <Skeleton className="h-9 w-9 rounded-md" />
                  ) : can("create") ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="icon"
                          onClick={handleNewItem}
                          className="h-9 w-9"
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>
                          {t("table.filters.add")} {tableName}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  ) : null)}
              </div>
            </div>

            {/* Filtros y controles (ocultos en móvil) */}
            <div className="hidden lg:flex flex-row gap-2">
              {filters
                .filter((f) => f.type !== "numeric_range")
                .map((filter) => (
                  <Tooltip key={filter.id}>
                    <TooltipTrigger asChild>
                      <div>
                        {filter.customComponent ? (
                          React.createElement(filter.customComponent, {
                            value:
                              search[filter.id] || filter.defaultValue || "all",
                            onChange: (value: string) => {
                              filterActionRef.current = true;
                              if (value === "all") {
                                updateFilters({ [filter.id]: undefined });
                              } else {
                                updateFilters({ [filter.id]: value });
                              }
                            },
                            options: filter.options || [],
                            filterName: filter.name,
                          })
                        ) : (
                          <Select
                            value={
                              filter.options?.find(
                                (option) => option.value === search[filter.id],
                              )?.value || filter.defaultValue
                            }
                            onValueChange={(value) => {
                              filterActionRef.current = true;
                              if (value === "all") {
                                updateFilters({ [filter.id]: undefined });
                              } else {
                                updateFilters({ [filter.id]: value });
                              }
                            }}
                          >
                            <SelectTrigger className="w-fit min-w-[70px] whitespace-nowrap">
                              {filter.icon && (
                                <DynamicIcon name={filter.icon} />
                              )}
                              <SelectValue placeholder={filter.name} />
                            </SelectTrigger>
                            <SelectContent>
                              {filter.options?.map((option) => (
                                <SelectItem
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{filter.name}</p>
                    </TooltipContent>
                  </Tooltip>
                ))}

              {/* Filtros de rango numérico */}
              {filters
                .filter((f) => f.type === "numeric_range")
                .map((filter) => {
                  const currentRange = getNumericRange(
                    numericRangesParam,
                    filter.id,
                  );

                  return (
                    <Tooltip key={filter.id}>
                      <TooltipTrigger asChild>
                        <div>
                          <NumericRangePicker
                            label={filter.name}
                            prefix={filter.prefix || "L. "}
                            suffix={filter.suffix || ""}
                            allowDecimals={filter.allowDecimals ?? true}
                            decimalPlaces={filter.decimalPlaces ?? 2}
                            initialMin={currentRange?.min ?? filter.min}
                            initialMax={currentRange?.max ?? filter.max}
                            onUpdate={({ range }) => {
                              handleNumericRangeUpdate(
                                filter.id,
                                range.min,
                                range.max,
                              );
                            }}
                          />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{filter.name}</p>
                      </TooltipContent>
                    </Tooltip>
                  );
                })}

              {/* Control de filas por página */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <Select
                      onValueChange={(value) => {
                        const numericValue = Number(value);
                        filterActionRef.current = true;
                        persistPageSize(numericValue);
                        updateFilters({ targetMaxRows: numericValue });
                      }}
                      value={String(resolvedPageSize)}
                    >
                      <SelectTrigger className="w-fit min-w-[50px] 2xl:min-w-[70px] whitespace-nowrap">
                        <SelectValue placeholder={t("table.filters.show")} />
                      </SelectTrigger>
                      <SelectContent>
                        {[5, 10, 20, 50].map((rows) => (
                          <SelectItem key={rows} value={rows.toString()}>
                            {rows}
                            <span className="hidden 2xl:inline">
                              {" "}
                              {t("table.filters.rows")}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("table.filters.showRows")}</p>
                </TooltipContent>
              </Tooltip>

              {/* Date Range Picker */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <DateRangePicker
                      key={`${targetDateFrom}-${targetDateTo}`}
                      onUpdate={(values) => {
                        filterActionRef.current = true;
                        updateFilters({
                          targetDateFrom: values.range.from.toString(),
                          targetDateTo: values.range?.to?.toString() || "",
                        });
                      }}
                      initialDateFrom={
                        targetDateFrom
                          ? new Date(Number(targetDateFrom))
                          : undefined
                      }
                      initialDateTo={
                        targetDateTo
                          ? new Date(Number(targetDateTo))
                          : undefined
                      }
                      align="start"
                      locale="en-GB"
                      showCompare={false}
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("table.filters.filterByDate")}</p>
                </TooltipContent>
              </Tooltip>

              {/* Reset Button */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    onClick={handleResetFilters}
                    className="min-w-[36px] shrink-0"
                  >
                    <RefreshCcwIcon className="h-4 w-4 opacity-50" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("table.filters.resetFilters")}</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </div>

          {/* Botones de acción en desktop */}
          <div className="hidden lg:flex flex-row gap-2 shrink-0">
            {headerActions.map((action, index) => {
              const Icon = action.icon;
              return (
                <Tooltip key={`desktop-action-${index}`}>
                  <TooltipTrigger asChild>
                    <Button
                      variant={action.variant ?? "outline"}
                      onClick={action.onClick}
                    >
                      {Icon && <Icon className="2xl:mr-1 h-4 w-4" />}
                      <span className="hidden 2xl:inline">{action.label}</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{action.label}</p>
                  </TooltipContent>
                </Tooltip>
              );
            })}

            {showImportButton &&
              onImportClick &&
              (permissionsLoading ? (
                <Skeleton className="h-9 w-9 2xl:w-24 rounded-md" />
              ) : can("create") ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="secondary" onClick={onImportClick}>
                      <Upload className="2xl:mr-1 h-4 w-4" />
                      <span className="hidden 2xl:inline">
                        {t("table.filters.import")}
                      </span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>
                      {t("table.filters.import")} {tableName}s
                    </p>
                  </TooltipContent>
                </Tooltip>
              ) : null)}

            {(setIsModalOpen || createRoute) &&
              (permissionsLoading ? (
                <Skeleton
                  className={`h-9 rounded-md ${filters.length <= 2 ? "w-24" : "w-9"}`}
                />
              ) : can("create") ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button type="button" onClick={handleNewItem}>
                      <Plus className="2xl:mr-1 h-5 w-5" />
                      {filters.length <= 2 && (
                        <span className="md:inline sm:inline lg:inline xl:inline 2xl:inline hidden">
                          {t("table.filters.add")}
                        </span>
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>
                      {t("table.filters.add")} {tableName}
                    </p>
                  </TooltipContent>
                </Tooltip>
              ) : null)}
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
};
